import React, { useEffect, useState } from 'react'
import * as S from './EditDatasetConfiguration.style'

import { formatDateTime } from 'helpers/formatDate/formatDate'
import { CButton } from 'tabs/data/components/CButton'
import { MAX_TEXT_LENGTH } from 'tabs/data/constants'
import { Prompt } from 'react-router-dom'
import Switch from 'uiKit/Switch'
import Paragraph from 'uiKit/texts/Paragraph'
import { ConfirmDisableLastActiveDataset } from './ConfirmDisableLastActiveDataset'
import { DatasetType } from '../../../../../api/generated'

type EditDatasetDescriptionProps = {
  description: string
  type: DatasetType
  mandatoryDataPoints?: string
  isActive: boolean
  powerBiId?: string
  updatedAt?: number
  isMoreThanOneActive: boolean
  onSubmit: (description: string, mandatoryDataPoints: string, isActive: boolean, powerBiId: string) => Promise<void>
  onClose: () => void
}

export const EditDatasetConfiguration: React.FC<EditDatasetDescriptionProps> = ({
  description: initialDescription,
  type,
  mandatoryDataPoints: initialDataPoints,
  isActive: initialActivity,
  powerBiId: initialPowerBiId,
  updatedAt,
  isMoreThanOneActive,
  onClose,
  onSubmit,
}) => {
  const [description, setDescription] = useState(initialDescription)
  const [dataPoints, setDataPoints] = useState(initialDataPoints)
  const [activity, setActivity] = useState(initialActivity)
  const [powerBiId, setPowerBiId] = useState(initialPowerBiId)

  const [errorText, setErrorText] = useState('')
  const [errorPowerBi, setPowerBiError] = useState('')
  const [errorAPI, setErrorAPI] = useState('')

  const [loading, setLoading] = useState(false)
  const [showConfirmModal, setShowConfirmModal] = useState(false)

  const anyChanges =
    description.trim() !== initialDescription.trim() ||
    dataPoints.trim() !== initialDataPoints.trim() ||
    activity !== initialActivity ||
    powerBiId?.trim() !== initialPowerBiId?.trim()

  const formDescriptionError = Boolean(errorText || errorAPI)
  const formPowerBiIdError = Boolean(errorPowerBi || errorAPI)

  const isSubmitButtonDisabled = loading || !description.trim() || formDescriptionError

  const handleDescriptionChange = e => {
    if (errorAPI) setErrorAPI('')

    const { value = '' } = e.target
    let errorText = ''

    if (value.length > MAX_TEXT_LENGTH) {
      errorText = `You've reached the max length for the text value — ${MAX_TEXT_LENGTH} characters`
    } else if (value.trim().length < 1) {
      errorText = 'The field is required'
    }

    setErrorText(errorText)
    setDescription(value)
  }

  const handleDataPointsChange = e => {
    const value = e.target.value
    if (value.length <= MAX_TEXT_LENGTH) {
      setDataPoints(value)
    }
  }

  const handleSubmit = async () => {
    const isValidForm = !errorText && description.trim()
    const isDeactivatingLastOne = initialActivity && !activity && !isMoreThanOneActive

    const isActivatingPowerBI = type === DatasetType.PowerBi && activity && !powerBiId?.trim()

    if (isActivatingPowerBI) {
      setPowerBiError('Power BI ID is required when activating a POWER BI dataset type.')
      return
    }

    if (!isValidForm || loading) return

    if (isDeactivatingLastOne) {
      setShowConfirmModal(true)
    } else {
      try {
        setLoading(true)
        await onSubmit(description.trim(), dataPoints.trim(), activity, powerBiId?.trim())
      } finally {
        setLoading(false)
      }
    }
  }

  useEffect(() => {
    window.onbeforeunload = anyChanges ? () => true : null
    return () => {
      window.onbeforeunload = null
    }
  }, [anyChanges])

  useEffect(() => {
    setDescription(initialDescription)
    setDataPoints(initialDataPoints)
    setActivity(initialActivity)
    setErrorText('')
    setErrorAPI('')
  }, [initialDescription, initialDataPoints, initialActivity])

  return (
    <S.Container>
      <Prompt
        when={anyChanges}
        message="Are you sure you want to leave this page without saving your current changes?"
      />

      <S.Form>
        <S.Section>
          <S.Label>Description</S.Label>
          <S.InputContainer isError={Boolean(formDescriptionError)}>
            <S.Textarea
              disabled={loading}
              placeholder="+ Insert description here"
              value={description}
              onChange={handleDescriptionChange}
              isError={Boolean(formDescriptionError)}
            />
            <S.Error>{formDescriptionError}</S.Error>
          </S.InputContainer>

          <S.Label>Question mandatory data points</S.Label>
          <S.InputContainer>
            <S.Textarea
              height="100px"
              disabled={loading}
              placeholder="+ Insert question mandatory data points here (e.g. Date, Region, Category)"
              value={dataPoints}
              onChange={handleDataPointsChange}
            />
          </S.InputContainer>

          {powerBiId !== null && (
            <>
              <S.Label>Power BI ID</S.Label>
              <S.TitleInput
                value={powerBiId}
                onChange={e => {
                  setPowerBiId(e.target.value)
                  if (powerBiId) setPowerBiError('')
                }}
                placeholder="Enter Power BI dataset ID"
                isError={Boolean(formPowerBiIdError)}
                disabled={loading}
              />
              <S.Error>{errorPowerBi}</S.Error>
            </>
          )}

          <S.ToggleSection>
            <S.ToggleRow>
              <S.ToggleLabel>Dataset Activation</S.ToggleLabel>
              <Switch
                label={activity ? 'Dataset is activated' : 'Dataset is deactivated'}
                checked={activity}
                onChange={() => setActivity(prev => !prev)}
              />
            </S.ToggleRow>
            <Paragraph style={{ margin: 0, fontSize: 14, color: '#555' }}>
              When activated, the chatbot uses data from this dataset version for answering questions.
            </Paragraph>
          </S.ToggleSection>
        </S.Section>

        <S.Footer>
          {updatedAt && (
            <small style={{ opacity: 0.75, marginRight: 'auto' }}>Updated at: {formatDateTime(updatedAt * 1000)}</small>
          )}
          <CButton color="secondary" disabled={loading} onClick={onClose}>
            Cancel
          </CButton>
          <CButton disabled={!anyChanges || isSubmitButtonDisabled} onClick={handleSubmit}>
            Save
          </CButton>
        </S.Footer>
      </S.Form>

      <ConfirmDisableLastActiveDataset
        open={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={async () => {
          try {
            await onSubmit(description.trim(), dataPoints.trim(), activity, powerBiId?.trim())
            setShowConfirmModal(false)
          } catch (e) {
            setLoading(false)
          }
        }}
      />
    </S.Container>
  )
}
